import { Box, Button, Flex, Input, InputGroup, Text } from "@chakra-ui/react";
import ExportButton from "./buttons/ExportButton";
import FilterPanel from "./panel/FilterPanel";
import TableContainer from "./table/TableContainer";
import PaginationComponent from "./panel/Pagination";
import { useColorModeValue } from "../ui/color-mode";
import { darkMode, lightMode } from "../../theme/config";
import { useEffect, useState, useCallback } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { loadListDataRequest } from "../../store/listSlice";
import { FaSearch } from "react-icons/fa";

export const ListComponent = ({
  path,
  borderColor,
  userId,
  columns,
  formatData,
  actions,
  statusOptions,
  categoryOptions,
  subCategoryOptions,
  exportButtonLabel,
  initialFilters,
  hideExport,
  hideDateRange,
  source,
  showSearchBar,
}) => {
  //const formattedData = formatData(data);
  const backgroundColor = useColorModeValue(lightMode.boxBackgroundColor, darkMode.boxBackgroundColor);

  const [searchValue, setSearchValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  const filters = useSelector((state) => state.filters[path] || {}, shallowEqual);
  const searchFilters = searchQuery ? { productSerial: searchQuery } : {};

  const dispatch = useDispatch();


  const { pageSize, offset, rows, loading, totalCount } = useSelector(
    (state) => state.list[path] || {
      rows: [],
      total: 0,
      pageSize: 10,
      offset: 0,
      loading: true,
    }
  );

  const currentPage = Math.floor(offset / pageSize) + 1;
  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  const debouncedSearch = useCallback((value) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setSearchQuery(value);
    }, 1000);

    setSearchTimeout(timeout);
  }, [searchTimeout]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setSearchQuery('');
  };

  useEffect(() => {
    dispatch(loadListDataRequest({
      key: path,
      filterConditions: {
        ...initialFilters,
        ...filters,
        ...searchFilters
      },
      offset: 0,
    }));
  }, [filters, searchQuery]);

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const handlePageChange = (page) => {
    const newOffset = (page - 1) * pageSize;
    dispatch(loadListDataRequest({
      key: path,
      filterConditions: { ...initialFilters, ...filters, ...searchFilters },
      offset: newOffset,
    }));
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      const newOffset = offset - pageSize;
      dispatch(loadListDataRequest({
        key: path,
        filterConditions: { ...initialFilters, ...filters, ...searchFilters },
        offset: newOffset,
      }));
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      const newOffset = offset + pageSize;
      dispatch(loadListDataRequest({
        key: path,
        filterConditions: { ...initialFilters, ...filters, ...searchFilters },
        offset: newOffset,
      }));
    }
  };

  return (
    <Box borderRadius="sm" p={3} mt={5} shadow="md">
      <Box>
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontWeight="bold">
            Advanced Filters
          </Text>
          {!hideExport &&
            <ExportButton
              data={source}
              filters={filters}
              fileType="text/csv"
              buttonLabel={exportButtonLabel}
              buttonProps={{ colorScheme: 'blue', variant: 'outline' }}
              backgroundColor={backgroundColor}
              disabled={loading || rows.length === 0}
              source={source}
            />
          }
        </Flex>
      </Box>

      {showSearchBar && (
        <Box mb={4}>
          <Flex gap={2} align="end">
            <Box flex="1" maxW="300px">
              <InputGroup startElement={<FaSearch />}>
                <Input
                  placeholder="Search by serial number..."
                  size="sm"
                  backgroundColor={backgroundColor}
                  style={{ borderColor: borderColor }}
                  value={searchValue}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </Box>

            <Flex gap={2}>
              {(searchValue || searchQuery) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleClearSearch}
                  disabled={loading}
                >
                  Clear
                </Button>
              )}
            </Flex>
          </Flex>

          {/* Search status indicator */}
          {searchQuery && (
            <Text fontSize="xs" color="gray.500" mt={1}>
              Searching for: "{searchQuery}" {loading && "(Loading...)"}
              {!loading && `(${totalCount} results found)`}
            </Text>
          )}
        </Box>
      )}

      <FilterPanel
        path={path}
        userId={userId}
        filters={filters}
        statusOptions={statusOptions}
        categoryOptions={categoryOptions}
        hideDateRange={hideDateRange}
        subCategoryOptions={subCategoryOptions}
      />

      <TableContainer
        showColumnBorder={true}
        columns={columns}
        rows={formatData(rows)}
        actions={actions}
      />

      <Flex justify="center" mt={4}>
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onPrev={handlePrev}
          onNext={handleNext}
          size="sm"
          colorScheme="blue"
        />
      </Flex>

    </Box>
  );
};